import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPost } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, UserPlus, X, BookOpen, Brain, Zap, Flame, Trophy, MessageCircle,
         TrendingUp, AlertTriangle, CheckCircle2, Calendar, Users, ChevronRight, GraduationCap } from 'lucide-react';
import { examModuleStatus } from '@/lib/premiumExams';
import { toast } from 'sonner';

function ChildCard({ child, onSelect, onUnlink }) {
  return (
    <Card className="p-5 rounded-2xl border-slate-100 hover:border-blue-200 hover:shadow-md transition-all cursor-pointer" data-testid={`child-card-${child.child_id}`} onClick={() => onSelect(child.child_id)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-heading text-lg font-bold">
            {(child.child_name || child.child_email || 'S')[0].toUpperCase()}
          </div>
          <div>
            <h3 className="font-heading text-base font-bold text-slate-800">{child.child_name || 'Student'}</h3>
            <p className="text-xs text-slate-400 font-body">{child.child_board} · Class {child.child_class}</p>
            <p className="text-[10px] text-slate-300 font-body">{child.child_school}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ChevronRight className="w-5 h-5 text-slate-300" />
        </div>
      </div>
    </Card>
  );
}

function ChildProgress({ childId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet(`/parent/child/${childId}/progress`).then(d => { setData(d); setLoading(false); }).catch(() => { toast.error('Failed to load progress'); setLoading(false); });
  }, [childId]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-center text-slate-400 py-10">No data available</p>;

  const { child, subjects, recent_activity } = data;

  return (
    <div className="space-y-5 animate-slide-up" data-testid="child-progress">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-blue-500 transition-colors font-body">
        <ArrowLeft className="w-4 h-4" /> Back to all children
      </button>

      {/* Child Header */}
      <Card className="p-5 rounded-2xl border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50/30">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-heading text-2xl font-bold">
            {(child.name || 'S')[0].toUpperCase()}
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-800">{child.name}</h2>
            <p className="text-sm text-slate-500 font-body">{child.board} · Class {child.class_level} · {child.school}</p>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-xs font-body text-amber-600 flex items-center gap-1"><Flame className="w-3.5 h-3.5" /> {child.streak_days} day streak</span>
              <span className="text-xs font-body text-blue-600 flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> {child.total_points} Veda Coins</span>
              <span className="text-xs font-body text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {child.topics_completed} topics</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Weekly Summary */}
      <Card className="p-5 rounded-2xl border-slate-100" data-testid="weekly-summary">
        <h3 className="font-heading text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-blue-500" /> This Week</h3>
        <div className="grid grid-cols-4 gap-3 text-center">
          <div>
            <p className="font-heading text-xl font-bold text-slate-800">{recent_activity.sessions_this_week}</p>
            <p className="text-[10px] text-slate-400 font-body">Chat Sessions</p>
          </div>
          <div>
            <p className="font-heading text-xl font-bold text-slate-800">{recent_activity.quizzes_this_week}</p>
            <p className="text-[10px] text-slate-400 font-body">Quizzes Taken</p>
          </div>
          <div>
            <p className="font-heading text-xl font-bold text-slate-800">{recent_activity.total_sessions}</p>
            <p className="text-[10px] text-slate-400 font-body">Total Sessions</p>
          </div>
          <div>
            <p className="font-heading text-xl font-bold text-slate-800">{recent_activity.total_flashcards}</p>
            <p className="text-[10px] text-slate-400 font-body">Flashcard Sets</p>
          </div>
        </div>
      </Card>

      {/* Subject-wise Progress */}
      <div data-testid="subject-progress">
        <h3 className="font-heading text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-emerald-500" /> Subject Progress</h3>
        {subjects.length === 0 ? (
          <Card className="p-6 rounded-2xl border-slate-100 text-center">
            <p className="text-sm text-slate-400 font-body">No subject activity yet. Your child hasn't started studying.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {subjects.map(s => (
              <Card key={s.subject} className="p-4 rounded-2xl border-slate-100" data-testid={`subject-${s.subject}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-heading text-sm font-semibold text-slate-700">{s.subject}</h4>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-slate-400 font-body">{s.total_chats} chats · {s.total_quizzes} quizzes</span>
                    {s.avg_quiz_score > 0 && (
                      <Badge className={`text-[10px] ${s.avg_quiz_score >= 70 ? 'bg-emerald-100 text-emerald-600' : s.avg_quiz_score >= 40 ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-600'}`}>
                        {s.avg_quiz_score}% avg
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(100, s.topics_covered * 10)}%` }} />
                  </div>
                  <span className="text-[10px] text-slate-400 font-body">{s.topics_covered} topics</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      {data.recent_sessions?.length > 0 && (
        <div>
          <h3 className="font-heading text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-amber-500" /> Recent Sessions</h3>
          <div className="space-y-2">
            {data.recent_sessions.map((s, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                <MessageCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-body text-slate-700 truncate">{s.subject}: {s.topic}</p>
                </div>
                <span className="text-[10px] text-slate-400 font-body flex-shrink-0">{s.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [showLink, setShowLink] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linking, setLinking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [examsLive, setExamsLive] = useState(false);

  useEffect(() => { examModuleStatus().then(s => setExamsLive(!!(s.module_enabled && s.entitled))); }, []);

  useEffect(() => {
    if (user?.role !== 'parent') return;
    loadChildren();
  }, [user]);

  const loadChildren = () => {
    apiGet('/parent/children').then(d => { setChildren(d.children || []); setLoading(false); }).catch(() => setLoading(false));
  };

  const handleLink = async () => {
    if (!linkEmail.trim()) return;
    setLinking(true);
    try {
      await apiPost('/parent/link-child', { child_email: linkEmail.trim() });
      toast.success('Child linked successfully!');
      setLinkEmail('');
      setShowLink(false);
      loadChildren();
    } catch (e) {
      toast.error(e.message || 'Failed to link child');
    }
    setLinking(false);
  };

  const handleUnlink = async (childId) => {
    try {
      await apiPost('/parent/unlink-child', { child_user_id: childId });
      toast.success('Child unlinked');
      loadChildren();
      setSelectedChild(null);
    } catch (e) {
      toast.error(e.message || 'Failed to unlink');
    }
  };

  if (user?.role !== 'parent') {
    return (
      <div className="max-w-2xl mx-auto px-4 pt-10 text-center">
        <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="font-heading text-xl font-bold text-slate-700 mb-2">Parent Dashboard</h2>
        <p className="text-sm text-slate-400 font-body mb-4">This dashboard is available for parent accounts only. Contact the admin to set up your parent account.</p>
        <Button onClick={() => navigate('/home')} className="rounded-full font-heading">Go to Home</Button>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-12" data-testid="parent-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Parent Dashboard</h1>
          <p className="text-sm text-slate-500 font-body">Monitor your child's learning progress</p>
        </div>
        {!selectedChild && (
          <Button data-testid="link-child-btn" onClick={() => setShowLink(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-full font-heading text-sm gap-1.5">
            <UserPlus className="w-4 h-4" /> Link Child
          </Button>
        )}
      </div>

      {/* Additive Premium link — hidden entirely when the Exam Planner module
          is off or the plan isn't entitled. Nothing existing is moved. */}
      {examsLive && (
        <button data-testid="parent-exams-link" onClick={() => navigate('/exams')}
          className="w-full flex items-center gap-3 mb-5 p-4 rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50/50 text-left hover:shadow-sm transition-all">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-4.5 h-4.5" />
          </span>
          <span className="min-w-0">
            <span className="block font-heading text-sm font-semibold text-slate-900">Exams &amp; portions</span>
            <span className="block text-[11px] text-slate-500 font-body">Add the next exam and tick the chapters in its portion</span>
          </span>
        </button>
      )}

      {/* Link Child Form */}
      {showLink && (
        <Card className="p-5 rounded-2xl border-blue-200 mb-5 animate-slide-up" data-testid="link-child-form">
          <h3 className="font-heading text-sm font-semibold text-slate-700 mb-3">Link a Child Account</h3>
          <p className="text-xs text-slate-400 font-body mb-3">Enter your child's Gurukul Veda email address. They must have a student account.</p>
          <div className="flex gap-2">
            <Input data-testid="child-email-input" type="email" value={linkEmail} onChange={e => setLinkEmail(e.target.value)}
              placeholder="child@example.com" className="flex-1 rounded-xl font-body" />
            <Button data-testid="confirm-link-btn" onClick={handleLink} disabled={linking}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full font-heading text-sm">
              {linking ? 'Linking...' : 'Link'}
            </Button>
            <Button onClick={() => setShowLink(false)} variant="outline" className="rounded-full text-sm font-heading">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Selected Child Progress */}
      {selectedChild ? (
        <ChildProgress childId={selectedChild} onBack={() => setSelectedChild(null)} />
      ) : (
        /* Children List */
        <div className="space-y-3">
          {children.length > 0 && (
            <button data-testid="parent-open-insight" onClick={() => navigate('/parent-insight')}
              className="w-full text-left rounded-2xl border border-indigo-200 bg-indigo-50/70 p-3.5 hover:border-indigo-300 transition-colors">
              <p className="font-heading text-sm font-semibold text-slate-800">Readiness insight →</p>
              <p className="text-xs text-slate-600 font-body">
                Exam readiness with its reasons, what's shaky, and the one or two things that would actually help.
              </p>
            </button>
          )}
          {children.length === 0 ? (
            <Card className="p-8 rounded-2xl border-slate-100 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-heading text-base font-semibold text-slate-600 mb-1">No Children Linked</h3>
              <p className="text-sm text-slate-400 font-body mb-4">Link your child's Gurukul Veda account to monitor their progress.</p>
              <Button data-testid="link-child-empty-btn" onClick={() => setShowLink(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-full font-heading text-sm gap-1.5">
                <UserPlus className="w-4 h-4" /> Link Child Account
              </Button>
            </Card>
          ) : (
            children.map(c => (
              <ChildCard key={c.child_id} child={c} onSelect={setSelectedChild} onUnlink={handleUnlink} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
